<template>
  <FeatureFlag feature="server_table_quotes_report">
    <template #featureEnabled>
      <ServerTable
        id="quotes-table"
        ref="quote_datatable"
        title="Quotes"
        :source="source"
        :config="config"
        :filters="filters"
      />
    </template>
    <template #featureDisabled>
      <DatatableReportV3
        id="quotes-table"
        ref="quote_datatable"
        title="Quotes"
        :source="source"
        :config="config"
        :filters="filters"
      />
    </template>
  </FeatureFlag>
</template>

<script>
import { customerClassificationColumns } from "@/lib/customerTypes";
import { EventBus } from "@/lib/event_bus.js";

import Badge from "@/components/Badge.vue";
import FeatureFlag from "@/components/FeatureFlag.vue";
import ServerTable from "@/components/ServerTable/ServerTable.vue";
import CONSTANTS from "@/Constants";
import QuoteActionsContextMenu from "@/Customer/Profile/QuoteActionsContextMenu.vue";
import JobOpportunityLink from "@/Job/Opportunity/JobOpportunityLink.vue";
import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import TagList from "@/Tags/TagList.vue";
import store from "@/vuex/store";

export default {
  components: {
    DatatableReportV3,
    FeatureFlag,
    ServerTable
  },
  props: {
    source: {
      type: String,
      default: ""
    },
    filters: {
      type: Array,
      default: () => []
    }
  },
  data() {
    // Status sorts by the hidden alpha_status column's position, which moves with
    // the classification columns.
    const classificationColumns = customerClassificationColumns();

    return {
      config: {
        responsive: true,
        order: [[0, "desc"]],
        columns: [
          {
            data: "quote_date",
            title: "Created Date",
            searchable: false,
            responsivePriority: 5
          },
          {
            data: "last_edited_at",
            title: "Last Edited Date",
            searchable: false,
            responsivePriority: 14
          },
          { data: "quote_link", title: "Quote", responsivePriority: 2 },
          {
            data: "sent_at",
            title: "Sent",
            orderable: false,
            searchable: false,
            className: "quote-sent-badge",
            responsivePriority: 7
          },
          { data: "created_by", title: "Created By", responsivePriority: 8 },
          ...classificationColumns,
          { data: "customer", responsivePriority: 1 },
          { data: "customer_email", title: "Email", responsivePriority: 15 },
          { data: "customer_phone", title: "Phone", responsivePriority: 9 },
          {
            data: "quote_status",
            orderData: 9 + classificationColumns.length,
            searchable: false,
            title: "Status",
            responsivePriority: 4
          },
          {
            data: "alpha_status",
            visible: false,
            searchable: false,
            responsivePriority: 22
          },
          {
            data: "location_name",
            title: "Location Name",
            orderable: false,
            responsivePriority: 20
          },
          {
            data: "service_address",
            title: "Service Address",
            orderable: false,
            responsivePriority: 19
          },
          {
            data: "job",
            createdCell: this.createLinkedCellClickHandler((_row, anchor) => {
              window.location = anchor.href;
            }),
            responsivePriority: 10
          },
          { data: "department", title: "Department", responsivePriority: 12 },
          {
            data: "tags",
            title: "Tags",
            orderable: false,
            searchable: false,
            responsivePriority: 21
          },
          {
            data: "invoice_link",
            title: "Invoice",
            createdCell: this.createLinkedCellClickHandler((_row, anchor) => {
              window.location = anchor.href;
            }),
            responsivePriority: 11
          },
          { data: "name", responsivePriority: 6 },
          { data: "summary", responsivePriority: 16 },
          {
            data: "item_count",
            title: "Items",
            className: "text-center",
            searchable: false,
            responsivePriority: 13
          },
          {
            data: "promo_code",
            title: "Promo Code",
            filterName: "promo_code",
            responsivePriority: 17
          },
          {
            data: "promo_code_amount",
            title: "Promo Code Amount",
            className: "text-right",
            searchable: false,
            responsivePriority: 18
          },
          {
            data: "total_cents",
            title: "Total",
            className: "text-right",
            searchable: false,
            aggregateTitle: CONSTANTS.AGGREGATE_HEADERS.total,
            footerClassName: "text-right",
            responsivePriority: 3
          },
          {
            data: "actions",
            title: "",
            orderable: false,
            className: "text-center th-no-text",
            searchable: false
          }
        ],
        vueComponents: [
          {
            rootId: "job-opportunity-modal-vue",
            component: JobOpportunityLink,
            options: {
              store
            }
          },
          {
            rootId: "quote-context-menu-vue",
            component: QuoteActionsContextMenu,
            options: {
              store
            }
          },
          {
            rootId: "tag-list-vue",
            component: TagList
          },
          {
            rootId: "badge-vue",
            component: Badge
          }
        ]
      }
    };
  },
  mounted() {
    EventBus.$on("refresh-quote-datatable", this.refreshData);
    EventBus.$on("datatable-edit-quote", this.redirectToQuote);
  },
  beforeUnmount() {
    EventBus.$off("refresh-quote-datatable", this.refreshData);
    EventBus.$off("datatable-edit-quote", this.redirectToQuote);
  },
  methods: {
    refreshData() {
      // The ref lives on whichever table the flag selected, so it is absent
      // until the flag resolves. A refresh event that lands in that window has
      // nothing to refresh yet — the table fetches on mount regardless.
      this.$refs.quote_datatable?.refreshTable();
    },
    createLinkedCellClickHandler(navigate) {
      return (td, _cellData, row) => {
        td.addEventListener("click", (e) => {
          const anchor = td.querySelector("a");
          if (!anchor) return;

          e.preventDefault();
          e.stopPropagation();
          navigate(row, anchor, e);
        });
      };
    },
    redirectToQuote({ quoteId, customerId }) {
      window.location = `/customers/${customerId}?quote_id=${quoteId}&tab=c_Quotes&editing=true`;
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(.quote-sent-badge .sera-badge) {
  margin-left: 0px;
  justify-content: center !important;
}
:deep(td .delegated-icon) {
  display: none;
}
</style>
